import { ApolloClient, ApolloLink, from, HttpLink, NormalizedCacheObject } from '@apollo/client';
import { CONSTANTS } from 'config/constants';
import { cache } from './cache';
import { onError } from '@apollo/client/link/error';
import { GraphQLError } from 'graphql';
import Cookies from 'js-cookie';
import { RetryLink } from '@apollo/client/link/retry';
import { logoutOnSessionError } from 'utils/logout';
import { reportApolloNetworkError } from 'error-reporting/error-reporting';
import { autoReloadToNewVersion, BUILD_ID, checkForNewVersion } from 'utils/app-version';
import { recordGraphQLErrors, recordNetworkError } from './recent-errors';

const { API_URI, TOKEN_KEY, SOCKET_ID_KEY } = CONSTANTS;

const http = new HttpLink({
    uri: API_URI,
    credentials: 'same-origin',
});

const middleware = new ApolloLink((operation, forward) => {
    if (operation.variables) {
        const omitTypename = (key: string, value: unknown) => {
            return key === '__typename' ? undefined : value;
        };
        operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
    }
    operation.setContext({
        headers: {
            authorization: Cookies.get(TOKEN_KEY) || '',
            'socket-id': sessionStorage.getItem(SOCKET_ID_KEY) || '',
            'client-name': 'denthub-frontend-app',
            'client-version': BUILD_ID || '1.0.0',
        },
    });
    return forward(operation);
});

// Report all GQl error, in case we forgot to use showBoundary()
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    // DD-3780: the help assistant sees the last few errors (no variables, no data).
    const responseHeaders = operation?.getContext?.()?.response?.headers;
    if (graphQLErrors?.length) {
        recordGraphQLErrors(operation?.operationName, graphQLErrors, responseHeaders);
    } else if (networkError) {
        recordNetworkError(operation?.operationName, networkError, responseHeaders);
    }
    if (graphQLErrors) {
        graphQLErrors.forEach((e: GraphQLError) => {
            logoutOnSessionError(e?.message);
        });
        // DD-3742: a query the schema rejects usually means this tab runs a bundle from before a
        // release that removed a field. Check right away instead of waiting for the next poll.
        if (graphQLErrors.some((e: GraphQLError) => e?.extensions?.code === 'GRAPHQL_VALIDATION_FAILED')) {
            checkForNewVersion(true).then((isOutdated) => {
                if (isOutdated) autoReloadToNewVersion();
            });
        }
    }
    if (networkError) {
        console.error(networkError);
        // A non-2xx response with GraphQL errors in its body arrives as both; the backend logs those.
        if (!graphQLErrors?.length) {
            reportApolloNetworkError(networkError, operation?.operationName);
        }
    }
});

// classify network errors
const classifyNetworkError = (err: any): 'aborted' | 'network' | 'other' => {
    if (!err) return 'other';
    if (err.name === 'AbortError') return 'aborted';
    const msg = String(err.message || '');
    if (
        msg.includes('Failed to fetch') ||
        msg.includes('Load failed') ||
        msg.includes('NetworkError') ||
        msg.includes('TypeError')
    ) {
        return 'network';
    }
    return 'other';
};

// Default: retries queries/subscriptions on network erros; NOT mutations
const retryLink = new RetryLink({
    delay: {
        initial: 250,
        max: 4000,
        jitter: true,
    },
    attempts: (count, operation, error) => {
        console.log(`Retry attempt #${count} for ${operation.operationName}`);
        // Only for network-type failures (not aborts, not GraphQL errors)
        const kind = classifyNetworkError(error);
        if (kind !== 'network') return false;

        // Do not retry mutations unless explicitly allowed
        const isMutation = operation.query.definitions.some(
            (d: any) => d.kind === 'OperationDefinition' && d.operation === 'mutation',
        );
        if (isMutation) return false;

        return count <= 2;
    },
});

export const apolloClient = new ApolloClient<NormalizedCacheObject>({
    link: from([errorLink, retryLink, middleware, http]),
    cache,
    defaultContext: {
        includeExtensions: true,
        tracing: true,
    },
    defaultOptions: {
        query: {
            context: {
                includeExtensions: true,
                tracing: true,
            },
        },
    },
    // We can add client side graphql type definitions here using typeDefs property
});
